/**
 * Pure shot-clock state machine used by the extended scoreboard.
 * No timers or audio in here — the hook drives it with `Date.now()` values.
 */

export const SHOT_BASE_MS = 45_000
export const SHOT_EXT_MS = 30_000
export const SHOT_MAX_MS = SHOT_BASE_MS + SHOT_EXT_MS

export type ShotPlayer = 1 | 2
export type ShotClockStatus = 'idle' | 'running' | 'paused' | 'expired'
export type BeepKind = 'loud' | 'short' | 'long'

export interface ShotClockState {
    status: ShotClockStatus
    activePlayer: ShotPlayer
    /** Valid while running. */
    endsAtMs: number
    /** Valid while idle / paused / expired. */
    remainingMs: number
    /** Index 0 = player 1. true while the one-time +30s is still unused. */
    extAvailable: [boolean, boolean]
    /**
     * Shots started in the current game (break shot = 0). The shot right after the
     * break gets the one-time extended time, shared by both players.
     */
    shotsStarted: number
}

export type ShotClockAction =
    | { type: 'toggle'; now: number }
    | { type: 'reset'; now: number }
    | { type: 'switch'; now: number; to?: ShotPlayer }
    | { type: 'extend'; now: number }
    | { type: 'tick'; now: number }
    | { type: 'resetGame' }
    | { type: 'hydrate'; activePlayer: ShotPlayer; extAvailable: [boolean, boolean] }

export const otherPlayer = (p: ShotPlayer): ShotPlayer => (p === 1 ? 2 : 1)

const idx = (p: ShotPlayer): 0 | 1 => (p === 1 ? 0 : 1)

export function initialShotClockState(): ShotClockState {
    return {
        status: 'idle',
        activePlayer: 1,
        endsAtMs: 0,
        remainingMs: SHOT_BASE_MS,
        extAvailable: [true, true],
        shotsStarted: 0,
    }
}

/** Milliseconds left right now, whatever the status. */
export function remainingAt(state: ShotClockState, now: number): number {
    if (state.status === 'running') return Math.max(0, state.endsAtMs - now)
    return state.remainingMs
}

/** Whole seconds shown on the clock ("ss"): rounds up so 0 only shows at expiry. */
export function displaySeconds(remainingMs: number): number {
    return Math.min(Math.ceil(Math.max(0, remainingMs) / 1000), SHOT_MAX_MS / 1000)
}

export function formatSeconds(seconds: number): string {
    return String(Math.max(0, seconds)).padStart(2, '0')
}

/** Which beep (if any) belongs to this displayed second. */
export function beepFor(seconds: number): BeepKind | null {
    if (seconds === 10) return 'loud'
    if (seconds >= 1 && seconds <= 5) return 'short'
    if (seconds === 0) return 'long'
    return null
}

export type ShotClockTone = 'normal' | 'warning' | 'critical'

export function toneFor(seconds: number): ShotClockTone {
    if (seconds <= 5) return 'critical'
    if (seconds <= 10) return 'warning'
    return 'normal'
}

/** Starts a new shot for `player`: 45s, except the one extended (75s) shot after the break. */
function startShot(state: ShotClockState, player: ShotPlayer, now: number): ShotClockState {
    const extended = state.shotsStarted === 1
    return {
        ...state,
        status: 'running',
        activePlayer: player,
        endsAtMs: now + (extended ? SHOT_MAX_MS : SHOT_BASE_MS),
        remainingMs: 0,
        shotsStarted: state.shotsStarted + 1,
    }
}

export function shotClockReducer(state: ShotClockState, action: ShotClockAction): ShotClockState {
    switch (action.type) {
        case 'toggle': {
            if (state.status === 'idle') {
                return startShot(state, state.activePlayer, action.now)
            }
            if (state.status === 'running') {
                return { ...state, status: 'paused', remainingMs: Math.max(0, state.endsAtMs - action.now) }
            }
            if (state.status === 'paused') {
                return { ...state, status: 'running', endsAtMs: action.now + state.remainingMs }
            }
            return state
        }
        case 'reset':
            // A pot starts a new shot for the same player.
            return startShot(state, state.activePlayer, action.now)
        case 'switch':
            return startShot(state, action.to ?? otherPlayer(state.activePlayer), action.now)
        case 'extend': {
            const i = idx(state.activePlayer)
            if (!state.extAvailable[i]) return state
            if (state.status !== 'running' && state.status !== 'paused') return state
            const current = remainingAt(state, action.now)
            const next = Math.min(current + SHOT_EXT_MS, SHOT_MAX_MS)
            if (next <= current) return state
            const ext: [boolean, boolean] = [state.extAvailable[0], state.extAvailable[1]]
            ext[i] = false
            if (state.status === 'running') {
                return { ...state, extAvailable: ext, endsAtMs: action.now + next }
            }
            return { ...state, extAvailable: ext, remainingMs: next }
        }
        case 'tick':
            if (state.status === 'running' && action.now >= state.endsAtMs) {
                return { ...state, status: 'expired', remainingMs: 0 }
            }
            return state
        case 'resetGame':
            return { ...initialShotClockState(), activePlayer: state.activePlayer }
        case 'hydrate':
            return { ...state, activePlayer: action.activePlayer, extAvailable: action.extAvailable }
        default:
            return state
    }
}
